import React, { useState } from 'react';
import type { BehaviorPrefs, ShortcutAction } from '../../global';
import type { ToastKind } from '../../components/Toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Switch } from '../../components/ui/Switch';
import { useLanguage } from '../../lib/i18n';
import { IS_WEB } from '../../lib/platform';
import { usePreferences } from './usePreferences';

interface BehaviorCardProps {
  onNotify: (kind: ToastKind, text: string) => void;
}

interface BehaviorRow {
  key: keyof BehaviorPrefs;
  title: string;
  description: string;
  /** Satırın altında sarı uyarı / gri not. */
  note?: string;
  warning?: string;
}

/** 'Control+Alt+Shift+R' → 'Ctrl+Alt+Shift+R' (kullanıcıya gösterilen hâl). */
function shortcutLabel(accelerator: string): string {
  return accelerator.replace('Control', 'Ctrl');
}

/**
 * Ayarlar > Genel > Davranış: açılışta başlatma, kayıt sesleri, genel
 * kısayollar, ekran paylaşımından gizleme ve kayıtta pencereyi yana alma.
 * Kısayol kombinasyonları ana süreçten geliyor (services/behavior.ts) —
 * etiket ile gerçek kayıt asla ayrışmıyor.
 */
export const BehaviorCard: React.FC<BehaviorCardProps> = ({ onNotify }) => {
  const { t } = useLanguage();
  const { prefs, savePrefs } = usePreferences();
  const [busyKey, setBusyKey] = useState<keyof BehaviorPrefs | null>(null);

  const behavior = prefs.behavior;
  const failed = (action: ShortcutAction): boolean => prefs.failedShortcuts.includes(action);
  const shortcutWarning = t(
    'Bu kısayol şu an kaydedilemedi — başka bir uygulama kullanıyor olabilir.',
    'This shortcut could not be registered — another app may be using it.'
  );

  const toggle = async (key: keyof BehaviorPrefs, value: boolean) => {
    setBusyKey(key);
    try {
      await savePrefs({ behavior: { ...behavior, [key]: value } });
    } catch (err) {
      onNotify('error', (err as Error).message || t('Ayar kaydedilemedi.', 'Could not save the setting.'));
    } finally {
      setBusyKey(null);
    }
  };

  const allRows: BehaviorRow[] = [
    {
      key: 'launchAtLogin',
      title: t('Açılışta TalkNote’u başlat', 'Launch TalkNote at login'),
      description: t(
        'Bilgisayar açıldığında TalkNote da açılsın; günün ilk toplantısını kaçırma.',
        'Open TalkNote when your computer starts so it is ready for your first meeting of the day.'
      ),
      note: t(
        'Kurulum paketi hazır olduğunda devreye girecek; şimdilik yalnızca tercihin saklanıyor.',
        'This takes effect once the installer is ready; for now only your preference is stored.'
      ),
    },
    {
      key: 'recordingSounds',
      title: t('Kayıt sesleri', 'Recording sounds'),
      description: t(
        'Kayıt başladığında, durduğunda, kaydedildiğinde ya da hata olduğunda kısa bir ses çalar.',
        'Play a short sound when a recording starts, stops, saves, or hits an error.'
      ),
    },
    {
      key: 'recordingShortcut',
      title: t(
        `Genel kayıt kısayolu (${shortcutLabel(prefs.shortcuts['toggle-recording'])})`,
        `Global recording shortcut (${shortcutLabel(prefs.shortcuts['toggle-recording'])})`
      ),
      description: t(
        `Başka bir uygulamadayken bile ${shortcutLabel(prefs.shortcuts['toggle-recording'])} ile kaydı başlat ya da durdur.`,
        `Start or stop a recording from anywhere with ${shortcutLabel(prefs.shortcuts['toggle-recording'])}, even while another app is focused.`
      ),
      warning: behavior.recordingShortcut && failed('toggle-recording') ? shortcutWarning : undefined,
    },
    {
      key: 'recordingNotesShortcut',
      title: t(
        `Kayıt notu kısayolu (${shortcutLabel(prefs.shortcuts['recording-notes'])})`,
        `Recording notes shortcut (${shortcutLabel(prefs.shortcuts['recording-notes'])})`
      ),
      description: t(
        `Kayıt sürerken her yerden ${shortcutLabel(prefs.shortcuts['recording-notes'])} ile not alanına geç; yazdıkların kayıt bitince notun "Notlarım" bölümüne düşer.`,
        `While recording, press ${shortcutLabel(prefs.shortcuts['recording-notes'])} from anywhere to jot notes about the meeting — they are saved onto the finished note's My notes.`
      ),
      warning: behavior.recordingNotesShortcut && failed('recording-notes') ? shortcutWarning : undefined,
    },
    {
      key: 'quickFindShortcut',
      title: t(
        `Hızlı bulma kısayolu (${shortcutLabel(prefs.shortcuts['quick-find'])})`,
        `Quick find shortcut (${shortcutLabel(prefs.shortcuts['quick-find'])})`
      ),
      description: t(
        `${shortcutLabel(prefs.shortcuts['quick-find'])} ile TalkNote öne gelir ve arama çubuğu açılır.`,
        `Bring TalkNote forward with search open from anywhere with ${shortcutLabel(prefs.shortcuts['quick-find'])}.`
      ),
      warning: behavior.quickFindShortcut && failed('quick-find') ? shortcutWarning : undefined,
    },
    {
      key: 'hideFromScreenShare',
      title: t('Ekran paylaşımından gizle', 'Hide from screen sharing'),
      description: t(
        'Varsayılan kapalı: TalkNote penceresi normal, paylaşılabilir bir pencere. Açarsan pencere ekran paylaşımlarında ve ekran kayıtlarında görünmez — toplantı kaydı alırken bile.',
        'Off by default, so the TalkNote window is a normal, capturable window. Turn it on to keep it out of every screen share and recording — including while you are recording a call.'
      ),
    },
  ];
  // Web: açılışta başlatma, genel kısayollar ve pencere gizleme tarayıcıda yok.
  const rows = IS_WEB ? allRows.filter((row) => row.key === 'recordingSounds') : allRows;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('Davranış', 'Behavior')}</CardTitle>
        <CardDescription>
          {IS_WEB
            ? t('Kayıt sırasında TalkNote’un nasıl davranacağı.', 'How TalkNote behaves while recording.')
            : t(
                'TalkNote’un arka planda nasıl davranacağı: kısayollar, sesler ve pencere.',
                'How TalkNote behaves in the background: shortcuts, sounds and the window.'
              )}
        </CardDescription>
      </CardHeader>
      <CardContent className="divide-y divide-border">
        {rows.map((row) => (
          <div key={row.key} className="flex items-start justify-between gap-6 py-4 first:pt-0 last:pb-0">
            <div className="min-w-0 space-y-1">
              <p className="text-sm font-medium leading-none">{row.title}</p>
              <p className="text-[0.8rem] leading-relaxed text-muted-foreground">{row.description}</p>
              {row.note && <p className="text-[0.8rem] leading-relaxed text-muted-foreground/80">{row.note}</p>}
              {row.warning && <p className="text-[0.8rem] font-medium text-amber-500">{row.warning}</p>}
            </div>
            <Switch
              checked={behavior[row.key]}
              disabled={busyKey === row.key}
              onChange={(next) => void toggle(row.key, next)}
              label={row.title}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
